import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  CalendarClock, RefreshCw, Loader, CheckCircle, XCircle, AlertTriangle, StopCircle, Ban, SlidersHorizontal, ArrowUpDown,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { Screen, PageHead, Panel, Field, Notice, Pill, Stats, Stat, Item, Empty, Loading, Pager } from '../components/gtpl/GtplKit';

const PAGE_SIZE = 50;

function fmtDateTime(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

// "How long inactive" control: a mode plus a number of days the user types. "Less than N" means
// N-1 or fewer whole days; "exactly N" pins both ends. An empty / invalid number applies no filter.
const DAYS_MODES = [
  { key: 'any', label: 'Inactive: any time' },
  { key: 'min', label: 'Inactive at least' },
  { key: 'max', label: 'Inactive less than' },
  { key: 'exact', label: 'Inactive exactly' },
];
function daysParams(mode, value) {
  const n = parseInt(value, 10);
  if (mode === 'any' || Number.isNaN(n) || n < 0) return {};
  if (mode === 'min') return { minDays: n };
  if (mode === 'max') return { maxDays: n - 1 };
  return { minDays: n, maxDays: n };
}

// STB-issue statuses, in dropdown order. `key` is what the API stores and `summaryKey` the counter
// in the list summary that goes with it. New accounts start as "No change"; Pending and Done are
// set by hand.
const STB_STATUSES = [
  { key: 'no_change', label: 'No change', summaryKey: 'noChange', tone: 'mute' },
  { key: 'pending', label: 'Pending', summaryKey: 'pending', tone: 'warn' },
  { key: 'done', label: 'Done', summaryKey: 'done', tone: 'ok' },
];
const statusMeta = (key) => STB_STATUSES.find((x) => x.key === key) || STB_STATUSES[0];

// Fields the whole list can be sorted by (server-side, so it covers every page, not just the one
// on screen). Same column again flips the direction.
const SORT_FIELDS = [
  { key: 'days', label: 'Days inactive' },
  { key: 'inactiveDate', label: 'Inactive since' },
  { key: 'name', label: 'Name' },
  { key: 'accountNumber', label: 'Account no.' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'entityCode', label: 'Entity' },
  { key: 'stbSerial', label: 'Box no.' },
  { key: 'stbModel', label: 'Box model' },
  { key: 'stbIssueStatus', label: 'STB issue' },
];

// CRM category shown for each account. `crm` comes from the API: null when the CRM has no customer
// with this CATV account number, else { issued, status, catvActive } for the customer that has it.
// Nothing is hidden on account of the CRM — this is only a label and a filter.
const CRM_FILTERS = [
  { key: '', label: 'CRM: all' },
  { key: 'issued', label: 'CRM: issued in CRM' },
  { key: 'not_issued', label: 'CRM: not in CRM' },
];
function crmStatusText(crm) {
  const status = crm.status ? crm.status.charAt(0).toUpperCase() + crm.status.slice(1) : 'Unknown';
  // The CRM keeps a separate CATV on/off switch; an Active customer with CATV off is worth seeing.
  return crm.status === 'active' && !crm.catvActive ? `${status}, CATV off` : status;
}

const EXCLUDED_MODELS_KEY = 'gtpl90days.excludedModels';

// Unticked models are remembered per browser, so the same ones stay hidden next visit. Storage
// can be unavailable (private window, blocked site data) — the page just starts with all ticked.
function loadExcludedModels() {
  try {
    const v = JSON.parse(localStorage.getItem(EXCLUDED_MODELS_KEY) || '[]');
    return Array.isArray(v) ? v.filter((m) => typeof m === 'string') : [];
  } catch { return []; }
}
function saveExcludedModels(list) {
  try { localStorage.setItem(EXCLUDED_MODELS_KEY, JSON.stringify(list)); } catch { /* not critical */ }
}

// Tick-list of box models: ticked = shown, unticked = hidden. Works on the *excluded* set (not
// the included one) so a model that first appears after a refresh is shown by default rather than
// silently hidden. The panel is position:fixed because the surrounding panel clips overflow.
function ModelFilter({ models, counts, excluded, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: Math.min(r.left, Math.max(8, window.innerWidth - 300)) });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  const excludedSet = new Set(excluded);
  const shownCount = models.filter((m) => !excludedSet.has(m)).length;
  const needle = q.trim().toLowerCase();
  const visible = needle ? models.filter((m) => m.toLowerCase().includes(needle)) : models;

  const toggle = (m) => onChange(excludedSet.has(m) ? excluded.filter((x) => x !== m) : [...excluded, m]);
  // "All" / "None" act on the models currently listed, so they respect the search box.
  const tickAll = () => onChange(excluded.filter((m) => !visible.includes(m)));
  const untickAll = () => onChange([...new Set([...excluded, ...visible])]);

  const label = shownCount === models.length ? 'All box models' : `Box models: ${shownCount} of ${models.length}`;

  return (
    <>
      <button ref={btnRef} type="button" className="form-control" onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox" aria-expanded={open}
        style={{ textAlign: 'left', cursor: 'pointer', fontWeight: shownCount === models.length ? 400 : 700 }}>
        {label} <span style={{ fontSize: '.7em', color: 'var(--text-muted)' }}>▾</span>
      </button>
      {open && (
        <div ref={panelRef} role="listbox" aria-label="Box models"
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 50, width: 290,
            background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', padding: 10,
          }}>
          <input className="form-control" placeholder="Find a model…" value={q} onChange={(e) => setQ(e.target.value)}
            style={{ marginBottom: 8 }} autoFocus />
          <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '.78rem' }}>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={tickAll}>Tick all</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={untickAll}>Untick all</button>
            <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', alignSelf: 'center' }}>{shownCount}/{models.length} shown</span>
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', borderTop: '1px solid var(--border)' }}>
            {visible.length === 0 ? (
              <div style={{ padding: '10px 2px', color: 'var(--text-muted)', fontSize: '.82rem' }}>No models match.</div>
            ) : visible.map((m) => (
              <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 2px', fontSize: '.84rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={!excludedSet.has(m)} onChange={() => toggle(m)} />
                <span style={{ flex: 1, opacity: excludedSet.has(m) ? 0.55 : 1 }}>{m}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>{counts[m] ?? 0}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// Every account on GTPL Saathi's "INACTIVE SUBSCRIBER LIST REPORT(1 DAY BACK)" with the box it
// holds, filterable by entity / box model / how long it has been inactive, plus an STB-issue
// pending/done column that staff maintain. The report itself has no bulk export, so "Refresh
// from GTPL" drives it in a headless browser entity by entity (see
// gtplSaathiService.scrapeInactiveSubscribers) and runs in the background. Accounts and their
// STB-issue status are stored in the CRM, so the list stays here between refreshes.
export default function Gtpl90DaysPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canRefresh = hasModuleRight('gtplSaathi', 'inactiveAccounts');
  const canMark = hasModuleRight('gtplSaathi', 'stbIssueUpdate');

  const [run, setRun] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, done: 0, noChange: 0 });
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [crmSummary, setCrmSummary] = useState({ issued: 0, notIssued: 0 }); // how many listed accounts the CRM has / doesn't
  const [options, setOptions] = useState({ entities: [], models: [], modelCounts: {} });
  const [loadingRows, setLoadingRows] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [entity, setEntity] = useState('');
  const [excludedModels, setExcludedModels] = useState(loadExcludedModels);
  const [daysMode, setDaysMode] = useState('any');
  const [daysInput, setDaysInput] = useState(''); // what's typed in the days box
  const [days, setDays] = useState(''); // the debounced value the list is actually filtered by
  const [stbIssue, setStbIssue] = useState('');
  const [crmFilter, setCrmFilter] = useState(''); // '' | 'issued' | 'not_issued'
  const [showGone, setShowGone] = useState(false);
  const [sort, setSort] = useState({ by: 'days', dir: 'desc' }); // longest-inactive first

  const pollRef = useRef(null);
  const reqRef = useRef(0);

  // Debounce the search box so every keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Same for the days box, so typing "30" isn't a request for "3" first.
  useEffect(() => {
    const t = setTimeout(() => { setDays(daysInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [daysInput]);

  const loadRows = useCallback(async () => {
    const reqId = ++reqRef.current;
    setLoadingRows(true);
    try {
      const params = {
        page, limit: PAGE_SIZE,
        ...daysParams(daysMode, days),
        sortBy: sort.by, sortDir: sort.dir,
        ...(search && { search }),
        ...(entity && { entity }),
        ...(excludedModels.length && { excludeModels: JSON.stringify(excludedModels) }),
        ...(stbIssue && { stbIssue }),
        ...(crmFilter && { crm: crmFilter }),
        ...(showGone && { inReport: false }),
      };
      const { data } = await gtplSaathiAPI.getInactiveRows(params);
      if (reqId !== reqRef.current) return; // a newer filter change already superseded this response
      setRows(data.rows || []);
      setSummary({ pending: 0, done: 0, noChange: 0, ...(data.summary || {}) });
      setCrmSummary({ issued: 0, notIssued: 0, ...(data.crmSummary || {}) });
      setPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      if (reqId === reqRef.current) toast.error(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      if (reqId === reqRef.current) setLoadingRows(false);
    }
  }, [page, search, entity, excludedModels, daysMode, days, stbIssue, crmFilter, showGone, sort]);

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getInactiveLatest();
      setRun(data.data || null);
      return data.data || null;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load refresh status');
      return null;
    }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const { data } = await gtplSaathiAPI.getInactiveOptions();
      setOptions({ entities: [], models: [], modelCounts: {}, ...(data.data || {}) });
    } catch { /* filters just stay empty */ }
  }, []);

  useEffect(() => { loadLatest(); loadOptions(); }, []); // eslint-disable-line
  useEffect(() => { loadRows(); }, [loadRows]);

  // While a refresh runs in the background, keep the status and the list current.
  const isRunning = run?.status === 'running';
  useEffect(() => {
    if (!isRunning) { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const latest = await loadLatest();
      loadRows();
      if (latest?.status !== 'running') loadOptions();
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [isRunning, loadLatest, loadRows, loadOptions]);

  const handleRefresh = async () => {
    setStarting(true);
    try {
      const { data } = await gtplSaathiAPI.runInactiveRefresh();
      setRun(data.data);
      toast.success('Refresh started — this runs in the background');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start refresh');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setStopping(true);
    try {
      const { data } = await gtplSaathiAPI.stopInactiveRefresh();
      setRun(data.data);
      toast.success('Stop requested — finishing the page currently in progress, then it\'ll halt');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to stop');
    } finally {
      setStopping(false);
    }
  };

  const handleStbIssue = async (row, status) => {
    if (status === row.stbIssueStatus) return;
    setSavingId(row._id);
    try {
      const { data } = await gtplSaathiAPI.setInactiveStbIssue(row._id, status);
      setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...data.data, daysInactive: r.daysInactive } : r)));
      // Keep the status counters honest without a full reload: one out of the old bucket, one into the new.
      const from = statusMeta(row.stbIssueStatus).summaryKey;
      const to = statusMeta(status).summaryKey;
      setSummary((sm) => ({ ...sm, [from]: Math.max(0, sm[from] - 1), [to]: sm[to] + 1 }));
      // Under a status filter the row no longer belongs on this list.
      if (stbIssue) loadRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update STB issue');
    } finally {
      setSavingId(null);
    }
  };

  const setFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };
  // A new sort field starts ascending — except Days, where the useful first view is the most days.
  const changeSortField = (field) => { setSort({ by: field, dir: field === 'days' ? 'desc' : 'asc' }); setPage(1); };
  const flipSort = () => { setSort((cur) => ({ ...cur, dir: cur.dir === 'asc' ? 'desc' : 'asc' })); setPage(1); };
  const changeExcludedModels = (list) => { setExcludedModels(list); saveExcludedModels(list); setPage(1); };
  // Picking a mode with an empty box starts at 90 — the usual cut-off — ready to be overtyped.
  const changeDaysMode = (e) => {
    const mode = e.target.value;
    setDaysMode(mode);
    if (mode !== 'any' && daysInput === '') { setDaysInput('90'); setDays('90'); }
    setPage(1);
  };
  const filtersActive = !!(searchInput || entity || excludedModels.length || stbIssue || crmFilter || daysMode !== 'any' || showGone);
  const clearFilters = () => {
    setSearchInput(''); setSearch(''); setEntity(''); changeExcludedModels([]); setStbIssue(''); setCrmFilter('');
    setDaysMode('any'); setDaysInput(''); setDays(''); setShowGone(false); setPage(1);
  };
  const pickStatus = (key) => { setStbIssue((cur) => (cur === key ? '' : key)); setPage(1); };

  const runTone = !run ? 'mute' : run.status === 'running' ? 'warn' : run.status === 'failed' ? 'bad' : run.status === 'cancelled' ? 'mute' : 'ok';
  const RunIcon = run?.status === 'running' ? Loader : run?.status === 'failed' ? XCircle : run?.status === 'cancelled' ? Ban : CheckCircle;
  const runLabel = !run ? '' : run.status === 'running'
    ? (run.cancelRequested ? 'Stopping…' : `Running${run.currentEntity ? ` (${run.currentEntity})` : ''}`)
    : run.status === 'failed' ? 'Failed' : run.status === 'cancelled' ? 'Stopped' : 'Completed';

  return (
    <Screen>
      <PageHead icon={CalendarClock} tone="warn" title="90 Days (Account)"
        sub="Inactive accounts from GTPL Saathi's report, with the box each one holds. Checked live against the CRM's CATV account numbers; nothing is hidden.">
        {isRunning && (
          <button type="button" className="btn btn-sm btn-danger" disabled={stopping || run?.cancelRequested || !canRefresh} onClick={handleStop}>
            <StopCircle size={14} />{run?.cancelRequested || stopping ? 'Stopping…' : 'Stop'}
          </button>
        )}
        <button type="button" className="btn btn-primary" disabled={starting || isRunning || !canRefresh} onClick={handleRefresh}>
          <RefreshCw size={15} style={starting || isRunning ? { animation: 'spin 1s linear infinite' } : undefined} />
          {isRunning ? 'Refreshing…' : starting ? 'Starting…' : 'Refresh from GTPL'}
        </button>
      </PageHead>

      {!canRefresh && <Notice tone="bad">You don't have permission to refresh this list.</Notice>}

      {!run ? (
        <Notice tone="warn">The list hasn't been fetched from GTPL yet — click "Refresh from GTPL". A refresh pages through every entity one screen at a time, can take several minutes, and keeps running if you leave this page.</Notice>
      ) : (
        <Notice tone={runTone} icon={RunIcon} title={`Last refresh: ${runLabel}`}>
          {run.totalScraped || 0} accounts scraped · {run.status === 'running' ? 'started' : 'finished'} {fmtDateTime(run.status === 'running' ? run.startedAt : run.completedAt)}
          {run.failMessage && <div style={{ marginTop: 4 }}>{run.status !== 'cancelled' && <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 4 }} />}{run.failMessage}</div>}
        </Notice>
      )}

      <Stats>
        {STB_STATUSES.map((st) => (
          <Stat key={st.key} tone={st.tone} value={summary[st.summaryKey]} label={`STB issue: ${st.label.toLowerCase()}`}
            active={stbIssue === st.key} onClick={() => pickStatus(st.key)} />
        ))}
        <Stat tone="info" value={crmSummary.issued} label="Issued in CRM"
          active={crmFilter === 'issued'} onClick={() => { setCrmFilter((c) => (c === 'issued' ? '' : 'issued')); setPage(1); }} />
      </Stats>

      <Panel icon={SlidersHorizontal} tone="info" title="Find & filter" collapsible open
        sub={`${pagination.total} accounts${filtersActive ? ' match' : ''}`}>
        <div className="gx-form">
          <div className="gx-grid">
            <Field label="Search">
              <input className="form-control" placeholder="Account, name, mobile, box no."
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            </Field>
            <Field label="Entity">
              <select className="form-control" value={entity} onChange={setFilter(setEntity)} aria-label="Entity">
                <option value="">All entities</option>
                {options.entities.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Box model"><ModelFilter models={options.models} counts={options.modelCounts} excluded={excludedModels} onChange={changeExcludedModels} /></Field>
            <Field label="How long inactive">
              <div className="gx-actions" style={{ flexWrap: 'nowrap' }}>
                <select className="form-control" value={daysMode} onChange={changeDaysMode} aria-label="How long inactive">
                  {DAYS_MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
                {daysMode !== 'any' && (
                  <input type="number" min="0" step="1" inputMode="numeric" className="form-control" style={{ width: 90, flex: '0 0 auto' }}
                    value={daysInput} onChange={(e) => setDaysInput(e.target.value)} aria-label="Number of days inactive" />
                )}
              </div>
            </Field>
            <Field label="STB issue">
              <select className="form-control" value={stbIssue} onChange={setFilter(setStbIssue)} aria-label="STB issue">
                <option value="">STB issue: all</option>
                {STB_STATUSES.map((st) => <option key={st.key} value={st.key}>STB issue: {st.label.toLowerCase()}</option>)}
              </select>
            </Field>
            <Field label="CRM">
              <select className="form-control" value={crmFilter} onChange={setFilter(setCrmFilter)} aria-label="CRM">
                {CRM_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="gx-actions">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '.85rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showGone} onChange={(e) => { setShowGone(e.target.checked); setPage(1); }} />
              No longer inactive
            </label>
            {filtersActive && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>Clear filters</button>}
          </div>
        </div>
      </Panel>

      <Panel icon={ArrowUpDown} tone="mute" title="Accounts" count={pagination.total}
        right={
          <span className="gx-actions" style={{ gap: 6, flexWrap: 'nowrap' }}>
            <select className="form-control" style={{ width: 'auto', padding: '4px 8px' }} value={sort.by} onChange={(e) => changeSortField(e.target.value)} aria-label="Sort by">
              {SORT_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={flipSort}
              title={`Sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'} — click to reverse`}>{sort.dir === 'asc' ? '▲' : '▼'}</button>
          </span>}>
        {loadingRows && rows.length === 0 ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title={filtersActive ? 'No accounts match these filters' : 'No accounts yet'}>
            {filtersActive ? null : 'Click "Refresh from GTPL" to fetch the inactive list.'}
          </Empty>
        ) : (
          <div className="gx-list" style={{ opacity: loadingRows ? 0.6 : 1 }}>
            {rows.map((r) => {
              const st = statusMeta(r.stbIssueStatus);
              return (
                <Item key={r._id} tone={st.tone === 'mute' ? undefined : st.tone}
                  title={r.name || '—'} sub={<><span style={{ fontFamily: 'monospace' }}>{r.accountNumber}</span>{r.mobile ? ` · ${r.mobile}` : ''}</>}
                  right={<Pill tone={r.daysInactive >= 90 ? 'bad' : 'warn'} title="Days inactive">{r.daysInactive ?? '—'} days</Pill>}
                  meta={<>
                    <span>{r.entityCode || '—'}</span>
                    <span style={{ fontFamily: 'monospace' }}>{r.stbSerial || '—'}</span>
                    <span>{r.stbModel || '—'}</span>
                    <span>Since {fmtDate(r.inactiveDate)}</span>
                    {r.crm ? (
                      <Pill tone="ok" title={`A customer in the CRM has this CATV account number. CRM status: ${crmStatusText(r.crm)}.`}>Issued in CRM · {crmStatusText(r.crm)}</Pill>
                    ) : (
                      <Pill tone="mute">Not in CRM</Pill>
                    )}
                  </>}
                  actions={
                    <select className="form-control" style={{ width: 'auto', fontWeight: 600 }}
                      aria-label={`STB issue for ${r.accountNumber}`}
                      value={r.stbIssueStatus || 'no_change'}
                      disabled={!canMark || savingId === r._id}
                      onChange={(e) => handleStbIssue(r, e.target.value)}
                      title={r.stbIssueUpdatedAt ? `Last set by ${r.stbIssueUpdatedBy || 'unknown'} on ${fmtDateTime(r.stbIssueUpdatedAt)}` : 'Not updated yet'}>
                      {STB_STATUSES.map((s) => <option key={s.key} value={s.key}>STB issue: {s.label}</option>)}
                    </select>} />
              );
            })}
          </div>
        )}
        {pagination.pages > 1 && <Pager page={page} pages={pagination.pages} total={pagination.total} onPage={setPage} />}
      </Panel>
    </Screen>
  );
}
